import { forEach, map, filter } from 'lodash';

import { concat, findByType } from 'gear';

import { Point, Matrix, Bounds } from '../../geometry';
import { Pen, Brush } from '../../style';
import { Process } from '../../process/process';

import { AbsoluteAnchor } from '../anchor';
import { Figure } from '../figure';
import { Figures } from '../figures';
import { updateArray$ } from '../detail/processor';

import { TextPart, TextRange } from './multi-part';

// Given a list of texts, combine them to create a multipart text.
// The phrase is given by the order of the parts. If two parts have the same line
// and they are overlapping, they are consider a shadow or contour effect.
// If one of the texts is already multipart, then its phrase will be used
// Calls callback.call(scope,multiPartText)

export const autoCreateMultiPartText$ = function( figures, parentMatrix22, parentPin ) {

  // Separte multipart texts [TODO] flatMap
  let texts = [] ;
  forEach( filter(figures,'isText'), t => {
    if( t.isMultiPart ) {
      texts = concat(texts,t.breakIntoParts());
    }
    else {
      texts.push(t);
    }
  });

  return updateArray$(texts) .then( () => {

    if( texts.length === 0 ) {
      return null;
    }
    if( texts.length === 1 ) {
      return texts[0].clone();
    }

    // We have at least two figures to dealt with

    Figures.lock(texts);

    const textParts = map( texts, text => ({ figure: text, frame: text.vectorFrame_() }) );

    const composedTransform = parentMatrix22 || Figures.composedIsometricMatrix22( texts );
    const composedPinPos    = parentPin      || Figures.meanPin( texts );

    // Transform the frames so we allow the user to feed us with transformed text chains
    const inverseComposedTransform = Matrix.invert(composedTransform);
    forEach( textParts, t => {
      t.frame.transform(inverseComposedTransform);
      t.pos = t.frame.interpolate({x:0.5,y:0.5});
    });

    // Find the composedLine and basic ranges checking if the parts are duplicates
    // or sequences of texts
    let composedLine = '';

    // Sort the parts horizontally, we ignore Z order

    const sortedTextParts = textParts.slice(0); // clone only the array, not the contents
    sortedTextParts.sort( (a,b) => a.pos.x-b.pos.x );

    const areSequenced = function(current,prev) {
      const haveSameLine = current.figure.text === prev.figure.text ;
      const overlapPercentage = Bounds.horizontalOverlapPercentage(current.frame.bounds,prev.frame.bounds);
                           // Frame.overlapPercentage(current.frame,prev.frame);
      const areOverlapping = overlapPercentage > 0.5;
      return ! ( haveSameLine && areOverlapping ) ;
    };

    let allLinesAreEqual = true;
    forEach( sortedTextParts, (current,k) => {
      if( k>0 && current.figure.text !== sortedTextParts[k-1].figure.text ) {
        allLinesAreEqual = false;
      }
    });

    let rangeStart=1;
    forEach( sortedTextParts, (current,k) => {
      const sequenced = k>0 && !allLinesAreEqual && areSequenced(current,sortedTextParts[k-1]);
      if( sequenced ) {
        const prevLine = sortedTextParts[k-1].figure.text ;
        rangeStart += prevLine.length;
        composedLine += prevLine;
      }
      const currentLine = current.figure.text;
      current.range = new TextRange({ start: rangeStart, end: (rangeStart+currentLine.length) });
    });
    const lastPart = sortedTextParts[sortedTextParts.length-1];
    const lastLine = lastPart.figure.text;
    composedLine += lastLine;

    // We need to correct the ranges so they will work later when the user change the line
    // with one of a different size.
    // If the last part only has one letter, we consider it a bookend effect
    // The range of the last letter will be {-1,0} so it always matches only the last one
    const bookendEffect = ( composedLine.length >= 3 ) && ( lastLine.length === 1 );
    if( bookendEffect ) {
      forEach( textParts, t => {
        if( t.range.end-1 === composedLine.length ) {
          t.range = TextRange.LastLetter();
        }
        else if( t.range.end === composedLine.length ) {
          t.range.end = -1;

          // For the middle range, we apply the same technique that TX was using in their
          // templates: adding an stretching envelope to force the content to fit the
          // current space. This will make the multipart text look good when the user
          // will select another phrase.
          const processes = t.figure._processes;
          let process = findByType(processes,Process.FitEnvelope) ;
          if( ! process ) {
            process = Process.FitEnvelope.createFromFigure(t.figure);
            processes.add(process);
            t.figure._fixUpPin$('getFirstEnvelopePoint');
            // This is an async operation, see comment [*] below
          }
        }
      });
    }
    else {
      // Set to match til the end of the line
      forEach( textParts, t => {
        if( t.range.end-1 === composedLine.length ) {
          t.range.end = 0;
        }
      });
    }

    // The order of the textParts must not be changed to respect the user Z order
    return Figure.createMultiPartText(composedLine,composedTransform,composedPinPos,textParts);
  });
};

// Given a line, a transform, a pinPos and a list of {figure,range} its creates a
// multipart text
Figure.createMultiPartText = function( line, matrix22, pin, figureParts ) {
  const textParts = map( figureParts, fp => {
    return TextPart.createFromText(fp.figure,fp.range,matrix22,pin);
  });
  return Figure.Text({
    brush: Brush.empty,
    pen:   Pen.empty,
    text:  line,
    anchor: AbsoluteAnchor( new Point(0,0) ),
    matrix22: matrix22,
    pin: pin,
    textParts: textParts
  });
};



/** WEBPACK FOOTER **
 ** ./src/cdl/figure/text/auto-create-multi-part.js
 **/